import { FolderOpen, Plus, Search } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CasesGridView } from '@/components/cases/CasesGridView'
import { CasesStatChipsView } from '@/components/cases/CasesStatChipsView'
import { PageShell } from '@/components/layout/PageShell'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CreateCaseDialogConnected, DeleteCaseDialogConnected } from '@/features/cases/connected'
import { useCases } from '@/features/cases/hooks/useCases'
import type { Case } from '@/services/api'

/**
 * Connected cases dashboard. Owns the case-list query, search/dialog state and
 * navigation, then composes the pure {@link CasesStatChipsView} /
 * {@link CasesGridView} presentation. Rendered by the `/cases/home` page.
 */
export function CasesDashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: cases = [], isLoading } = useCases()

  const [searchQuery, setSearchQuery] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [caseToDelete, setCaseToDelete] = useState<Case | null>(null)

  const filtered = cases.filter(
    c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchQuery.toLowerCase())
  )
  const activeCount = cases.filter(c => c.status === 'active').length

  return (
    <PageShell
      layout="padded"
      icon={FolderOpen}
      title={t('cases.dashboard.title')}
      summary={t('cases.dashboard.summary')}
      guide={{
        to: '/docs/case-management#creating-a-case',
        label: t('cases.dashboard.guideLabel'),
        hint: t('cases.dashboard.guideHint'),
      }}
      toolbar={
        <Button
          size="sm"
          onClick={() => setCreateOpen(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 max-md:h-9"
        >
          <Plus className="h-4 w-4 mr-1.5" />
          {t('cases.dashboard.newCase')}
        </Button>
      }
    >
      <CasesStatChipsView total={cases.length} activeCount={activeCount} />

      <div>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="text-[11px] font-medium text-muted-foreground uppercase tracking-widest whitespace-nowrap">
            {t('cases.dashboard.allCases')}
          </h2>
          <div className="flex-1 border-t border-border" />
        </div>
        <div className="relative md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('cases.dashboard.searchPlaceholder')}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 bg-card/50 border-border text-foreground placeholder:text-muted-foreground"
          />
        </div>
      </div>

      <CasesGridView
        cases={filtered}
        loading={isLoading}
        hasSearch={searchQuery.length > 0}
        onOpen={id => navigate(`/cases/${id}`)}
        onDelete={c => {
          setCaseToDelete(c)
          setDeleteOpen(true)
        }}
        onCreateFirst={() => setCreateOpen(true)}
      />

      <CreateCaseDialogConnected open={createOpen} onOpenChange={setCreateOpen} />
      <DeleteCaseDialogConnected
        open={deleteOpen}
        onOpenChange={open => {
          setDeleteOpen(open)
          if (!open) setCaseToDelete(null)
        }}
        caseData={caseToDelete}
      />
    </PageShell>
  )
}
